import { Link } from 'react-router-dom'
import type { CSSProperties, ReactNode } from 'react'
import { useTileInteraction } from '../hooks/useTileInteraction'

interface TileFrameProps {
  /** The tile's colour, as an `r, g, b` triple from the mark's own spectrum. */
  glow: string
  /** Where it opens. Omitted for a tile with nothing behind it yet. */
  to?: string
  /** What the link is called, for anyone not looking at the screen. */
  label: string
  pinned?: boolean
  onTogglePin?: () => void
  onDelete?: () => void
  /** Open `to` in a new tab. Projects do (Huanrui, 2026-09-30). */
  newTab?: boolean
  /** A share button beside the pin, for projects. */
  onShare?: () => void
  className?: string
  children: ReactNode
}

/**
 * The shared shell of every tile on the overview.
 *
 * WHY THE TILE IS A DIV AND THE LINK IS A LAYER
 * It used to be one `<a>` with everything inside it. That stopped working the
 * moment a tile needed its own buttons: a button inside an anchor is invalid
 * HTML, the click lands on both, and the keyboard order reads as one control
 * containing others. So the link is now a transparent layer stretched across
 * the tile, the buttons sit above it, and the text sits below — three layers,
 * each doing one thing.
 *
 * The hover behaviour is unchanged and still lives in useTileInteraction.
 */
export function TileFrame({
  glow,
  to,
  label,
  pinned = false,
  onTogglePin,
  onDelete,
  onShare,
  newTab = false,
  className = '',
  children,
}: TileFrameProps) {
  const { ref, ripples, handlers } = useTileInteraction<HTMLDivElement>({
    interactive: Boolean(to),
  })

  const style = { '--tile-glow': glow } as CSSProperties

  return (
    <div
      ref={ref}
      className={`ww-tile${pinned ? ' ww-tile--pinned' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      {...handlers}
    >
      {/* Both layers sit under the text and take no pointer events, so the
          cursor never leaves the tile by moving onto its own decoration. */}
      <span className="ww-tile__spot" aria-hidden="true" />
      <span className="ww-tile__edge" aria-hidden="true" />

      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="ww-tile__ripple"
          aria-hidden="true"
          style={{ left: ripple.x, top: ripple.y }}
        />
      ))}

      {children}

      {to ? (
        <Link
          className="ww-tile__open"
          to={to}
          aria-label={label}
          {...(newTab ? { target: '_blank', rel: 'noopener' } : {})}
        />
      ) : null}

      {onDelete ? (
        <button
          type="button"
          className="ww-tile__act ww-tile__act--delete"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onDelete()
          }}
          aria-label={`Delete ${label}`}
          title="Delete"
        >
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M2.5 3.5h9M5.5 3.5V2.2h3v1.3M3.7 3.5l.5 8.3h5.6l.5-8.3M6 6v3.4M8 6v3.4"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}

      {onShare ? (
        <button
          type="button"
          className="ww-tile__act ww-tile__act--share"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onShare()
          }}
          aria-label={`Share ${label}`}
          title="Share a copy"
        >
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M7 1.8v7.2M4.3 4.3 7 1.6l2.7 2.7M3.2 6.6H2.6a.8.8 0 0 0-.8.8v4.2c0 .4.4.8.8.8h8.8c.4 0 .8-.4.8-.8V7.4a.8.8 0 0 0-.8-.8h-.6"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}

      {onTogglePin ? (
        <button
          type="button"
          className={`ww-tile__act ww-tile__act--pin${pinned ? ' ww-tile__act--on' : ''}`}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onTogglePin()
          }}
          aria-pressed={pinned}
          aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
          title={pinned ? 'Unpin' : 'Pin to top'}
        >
          <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true">
            {/* Filled when pinned, outlined when not: the state is readable
                without colour, which is the only way it works for everyone. */}
            <path
              d="M8.6 1.6 12.4 5.4l-1.6.5-1 1-.3 2.6-1.2 1.2-2.6-2.6-2.9 2.9-.6-.6 2.9-2.9L2.5 4.9l1.2-1.2 2.6-.3 1-1z"
              fill={pinned ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="1.1"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
    </div>
  )
}
